// Single source of truth for the section-navigation tree (NAV-IA).
//
// MODES / TabDef / ModeDef and the active-path matcher were extracted out of
// ViewTabs.tsx so that every nav surface — the tab strip, the command
// palette's "Go" group, the g-chord keyboard bindings and the shortcut help
// sheet — derives from ONE tree and ONE chord map. There is exactly one place
// to add a route or rebind a chord.
//
// tabMemory.ts imports from here (NAV_TREE + isActivePath); this module must
// NOT import from tabMemory.ts (no cycle).

import {
  Activity,
  AlertTriangle,
  BatteryCharging,
  Calendar,
  Cpu,
  Gauge,
  History,
  LineChart,
  Settings,
  Wallet,
  Zap,
  type LucideIcon,
} from "lucide-react";

export type TabDef = {
  path: string;
  label: string;
  icon: LucideIcon;
  /** Exact pathnames that mark this tab active. */
  matchPaths: string[];
  /** Pathname prefixes that also mark this tab active (drill-down routes). */
  matchPrefix?: string[];
  /** Carry the alarm-status indicator on this tab. */
  showsAlarmBadge?: boolean;
};

export type ModeDef = {
  id: "monitor" | "manage" | "analyze";
  label: string;
  /** One-line "why this mode" intent, passed straight to t() (NOT a NAV_LABEL_KEY). */
  intentKey: string;
  /** Landing tab when the mode pill is clicked. */
  defaultPath: string;
  tabs: TabDef[];
};

export const MONITOR: ModeDef = {
  id: "monitor",
  label: "Monitor",
  intentKey: "monitor.nav.modeIntent.monitor",
  defaultPath: "/overview",
  tabs: [
    { path: "/overview", label: "Overview", icon: Gauge, matchPaths: ["/overview"] },
    { path: "/flow", label: "Energy flow", icon: Activity, matchPaths: ["/flow"] },
    {
      path: "/bess",
      label: "BESS",
      icon: BatteryCharging,
      matchPaths: ["/bess"],
      matchPrefix: ["/bess/"],
    },
    {
      path: "/alarms",
      label: "Alarms",
      icon: AlertTriangle,
      matchPaths: ["/alarms"],
      matchPrefix: ["/alarms/rules"],
      showsAlarmBadge: true,
    },
  ],
};

export const MANAGE: ModeDef = {
  id: "manage",
  label: "Manage",
  intentKey: "monitor.nav.modeIntent.manage",
  defaultPath: "/schedule",
  tabs: [
    { path: "/schedule", label: "Schedule", icon: Calendar, matchPaths: ["/schedule"] },
    { path: "/controller", label: "Controller", icon: Cpu, matchPaths: ["/controller"] },
    { path: "/site", label: "Site config", icon: Settings, matchPaths: ["/site"] },
  ],
};

export const ANALYZE: ModeDef = {
  id: "analyze",
  label: "Analyze",
  intentKey: "monitor.nav.modeIntent.analyze",
  defaultPath: "/historical",
  tabs: [
    { path: "/historical", label: "Historical", icon: History, matchPaths: ["/historical"] },
    { path: "/analytics", label: "Analytics", icon: LineChart, matchPaths: ["/analytics"] },
    { path: "/revenue", label: "Revenue", icon: Wallet, matchPaths: ["/revenue"] },
    {
      path: "/market-price",
      label: "Market price",
      icon: Zap,
      matchPaths: ["/market-price"],
    },
  ],
};

export const MODES: ModeDef[] = [MONITOR, MANAGE, ANALYZE];

/** Alias used by consumers that read the whole tree (palette, tab memory). */
export const NAV_TREE: ModeDef[] = MODES;

// English labels above stay the data/identity (tab memory, matching);
// translation happens at render via the dict.common nav keys. Unknown
// labels fall through t() unchanged (t returns the key itself).
export const NAV_LABEL_KEY: Record<string, string> = {
  Monitor: "nav.monitor",
  Manage: "nav.manage",
  Analyze: "nav.analyze",
  Overview: "nav.overview",
  "Energy flow": "nav.energyFlow",
  BESS: "nav.bess",
  Alarms: "nav.alarms",
  "Alarm rules": "nav.alarmRules",
  Schedule: "nav.schedule",
  Controller: "nav.controller",
  "Site config": "nav.siteConfig",
  Historical: "nav.historical",
  Analytics: "nav.analytics",
  Revenue: "nav.revenue",
  "Market price": "nav.marketPrice",
};

// The ONE chord source (NAV-IA): keyed by path, the inverse of the old
// NAV_BINDINGS. useKeyboardShortcuts builds its key→path map from this; the
// palette + shortcut help derive their chord hints from it.
export const NAV_CHORD: Record<string, string> = {
  "/overview": "o",
  "/flow": "f",
  "/bess": "b",
  "/historical": "h",
  "/analytics": "l",
  "/revenue": "r",
  "/market-price": "p",
  "/alarms": "a",
  "/schedule": "s",
  "/controller": "c",
  "/site": "i",
};

export function isActivePath(tab: TabDef, pathname: string): boolean {
  if (tab.matchPaths.includes(pathname)) return true;
  return tab.matchPrefix?.some((p) => pathname.startsWith(p)) ?? false;
}
